import { initializeApp } from 'firebase/app';
import {
  EmailAuthProvider,
  GithubAuthProvider,
  GoogleAuthProvider,
  TwitterAuthProvider,
  User,
  UserCredential,
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  deleteUser,
  getAuth,
  onAuthStateChanged,
  reauthenticateWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
} from 'firebase/auth';
import { getPerformance } from 'firebase/performance';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from 'src/api/auth';
import {
  setAnalyticsUserId,
  startAnalyticsConsentBridge,
} from 'src/analytics/analytics';
// config
import { FIREBASE_API } from '../config-global';
//
import { LOCAL_STORAGE_KEYS } from 'src/constants/localStorage';
import { clearAllAssistantChatCaches } from 'src/sections/@dashboard/visit/utils/assistantChatCache';
import { PATH_AUTH } from 'src/routes/paths';
import {
  bindOperationKeysToAccount,
  clearOperationKeyRegistry,
} from 'src/sections/@dashboard/warehouse/hooks/useOperationKey';
import {
  ActionMapType,
  AuthStateType,
  AuthUserType,
  FirebaseContextType,
} from './types';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
}

type Payload = {
  [Types.INITIAL]: {
    isAuthenticated: boolean;
    user: AuthUserType;
  };
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext<FirebaseContextType | null>(null);

// ----------------------------------------------------------------------

const app = initializeApp(FIREBASE_API);

// Performance monitoring needs a project; without one (unit tests in CI) it
// would throw while this module loads.
if (FIREBASE_API.projectId) getPerformance(app);

const GOOGLE_PROVIDER = new GoogleAuthProvider();

const GITHUB_PROVIDER = new GithubAuthProvider();

const TWITTER_PROVIDER = new TwitterAuthProvider();

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const queryClient = useQueryClient();

  // LOGOUT
  const logout = useCallback(async () => {
    setAnalyticsUserId(null);
    // Clear organization-specific localStorage items
    Object.values(LOCAL_STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
    clearAllAssistantChatCaches();

    // Drop any per-user React Query caches so the next login doesn't start
    // with the previous account's data. Without this, AuthGuard reads the
    // stale `['user']` snapshot during the auth-state flip and routes the
    // new session to the old role's landing page (e.g. user → /profiles
    // when logging in as a professional).
    queryClient.clear();
    clearOperationKeyRegistry();

    await signOut(getAuth());
  }, [queryClient]);

  const initialize = useCallback(() => {
    try {
      onAuthStateChanged(getAuth(), async (user) => {
        // R1-2: also reports sign-ins and sign-outs made in other tabs.
        bindOperationKeysToAccount(user?.uid ?? null);
        if (user) {
          // Allow authenticated users who are either:
          // 1. Email verified (traditional flow)
          // 2. Phone authenticated (phone number exists, no email or empty email)
          const isEmailVerified = user.emailVerified;
          const isPhoneAuthenticated =
            (!user.email || user.email === '') && !!user.phoneNumber;

          if (isEmailVerified || isPhoneAuthenticated) {
            setAnalyticsUserId(user.uid);
            dispatch({
              type: Types.INITIAL,
              payload: {
                ...state,
                isAuthenticated: true,
                user: {
                  ...user,
                },
              },
            });
          } else {
            setAnalyticsUserId(null);
            dispatch({
              type: Types.INITIAL,
              payload: {
                ...state,
                isAuthenticated: false,
                user: null,
              },
            });

            await signOut(getAuth());
            window.location.replace(
              window.location.pathname === PATH_AUTH.professionalLogin
                ? PATH_AUTH.professionalConfirmation
                : PATH_AUTH.confirmation,
            );
          }
        } else {
          setAnalyticsUserId(null);
          dispatch({
            type: Types.INITIAL,
            payload: {
              ...state,
              isAuthenticated: false,
              user: null,
            },
          });
        }
      });
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(startAnalyticsConsentBridge, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(
    async (email: string, password: string): Promise<User> => {
      const userCredentials = await signInWithEmailAndPassword(
        getAuth(),
        email,
        password,
      );

      return userCredentials.user;
    },
    [],
  );

  // AuthGuard sits below OrganizationStorageBoundary, which remounts it once
  // the organization storage is ready, with `?token=` still in the URL. A
  // token is exchanged once per page load: a repeated call shares the first
  // exchange while its account is still signed in. A second exchange would be
  // orphaned by the guard's landing redirect, and its failure (e.g. aborted by
  // a reload) would send the signed-in user to the login page. A failed
  // exchange is not remembered.
  const customTokenSignIn = useRef<{
    token: string;
    user: Promise<User | null>;
  } | null>(null);

  const loginWithCustomToken = useCallback(
    async (customToken: string): Promise<User | null> => {
      const earlier = customTokenSignIn.current;
      if (earlier?.token === customToken) {
        const earlierUser = await earlier.user;
        if (earlierUser && getAuth().currentUser?.uid === earlierUser.uid) {
          return earlierUser;
        }
      }

      const user = (async (): Promise<User | null> => {
        try {
          const userCredential = await signInWithCustomToken(
            getAuth(),
            customToken,
          );

          dispatch({
            type: Types.INITIAL,
            payload: {
              isAuthenticated: true,
              user: userCredential.user,
            },
          });

          return userCredential.user;
        } catch (error) {
          return null;
        }
      })();
      customTokenSignIn.current = { token: customToken, user };
      return user;
    },
    [],
  );

  const loginWithGoogle = useCallback(async (): Promise<UserCredential> => {
    return signInWithPopup(getAuth(), GOOGLE_PROVIDER);
  }, []);

  const loginWithGithub = useCallback(() => {
    signInWithPopup(getAuth(), GITHUB_PROVIDER);
  }, []);

  const loginWithTwitter = useCallback(() => {
    signInWithPopup(getAuth(), TWITTER_PROVIDER);
  }, []);

  // REGISTER
  const register = useCallback(async (email: string, password: string) => {
    dispatch({
      type: Types.INITIAL,
      payload: {
        isAuthenticated: false,
        user: null,
      },
    });

    return createUserWithEmailAndPassword(getAuth(), email, password);
  }, []);

  // DELETE
  const removeUser = useCallback(async (user: User) => {
    await deleteUser(user);
  }, []);

  // SEND RESET PASSWORD LINK
  // Routes through our backend so the user gets the Vetsi-branded
  // `passwordReset` email instead of Firebase's hosted template. The backend
  // calls Firebase Admin's `generatePasswordResetLink` and ships the link via
  // our mail pipeline. Endpoint is unauthenticated and intentionally returns
  // 200 whether or not the email is registered (no account enumeration).
  const sendPasswordResetLink = useCallback(async (email: string) => {
    await authApi.authControllerSendPasswordReset({
      sendPasswordResetDto: { email },
    });
  }, []);

  // CONFIRM PASSWORD RESET
  const resetPassword = useCallback(
    async (oobCode: string, newPassword: string) => {
      const auth = getAuth();
      if (!oobCode && !newPassword) return;
      return await confirmPasswordReset(auth, oobCode, newPassword);
    },
    [],
  );
  // UPDATE PASSWORD
  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string) => {
      const user = getAuth().currentUser;

      if (user?.email) {
        const credential = EmailAuthProvider.credential(
          user.email,
          oldPassword,
        );

        await reauthenticateWithCredential(user, credential);
        await updatePassword(user, newPassword);
      }
    },
    [],
  );

  const verifyEmail = useCallback(async (oobCode: string) => {
    await applyActionCode(getAuth(), oobCode);
  }, []);

  const loginWithPhone = useCallback(
    async (
      phoneNumber: string,
      recaptchaVerifier: RecaptchaVerifier,
    ): Promise<ConfirmationResult> => {
      return await signInWithPhoneNumber(
        getAuth(),
        phoneNumber,
        recaptchaVerifier,
      );
    },
    [],
  );

  const verifyPhoneCode = useCallback(
    async (
      confirmationResult: ConfirmationResult,
      code: string,
    ): Promise<UserCredential> => {
      return await confirmationResult.confirm(code);
    },
    [],
  );

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'firebase',
      login,
      loginWithGoogle,
      loginWithGithub,
      loginWithTwitter,
      register,
      logout,
      removeUser,
      changePassword,
      verifyEmail,
      sendPasswordResetLink,
      resetPassword,
      loginWithCustomToken,
      loginWithPhone,
      verifyPhoneCode,
    }),
    [
      state.isAuthenticated,
      state.isInitialized,
      state.user,
      login,
      loginWithGithub,
      loginWithGoogle,
      loginWithTwitter,
      register,
      logout,
      removeUser,
      changePassword,
      verifyEmail,
      sendPasswordResetLink,
      resetPassword,
      loginWithCustomToken,
      loginWithPhone,
      verifyPhoneCode,
    ],
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}
